import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TriangleAlert, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

/**
 * Sticky banner on /app/* surfacing platform-subscription status: trial
 * countdown, grace-mode lock, or a past-due card warning. Silent when the
 * academy is comped or billing is in good standing — we don't clutter the
 * UI when there's nothing the owner needs to act on.
 */
export default function TrialBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (user?.role !== "owner") return;
    api.get("/saas/status").then(r => setStatus(r.data)).catch(() => {});
  }, [user?.role]);

  if (!status || status.comped) return null;

  const daysLeft = status.trial_ends_at
    ? Math.ceil((new Date(status.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  if (status.locked) {
    return (
      <Banner testid="trial-banner-locked" tone="danger" Icon={TriangleAlert}
        text="Your RollOSS trial has ended. Subscribe to keep adding students, classes, and announcements." />
    );
  }
  if (status.billing_status === "past_due") {
    return (
      <Banner testid="trial-banner-past-due" tone="danger" Icon={TriangleAlert}
        text="We couldn't charge your card. Update your payment method to avoid losing access." />
    );
  }
  if (!status.has_subscription && daysLeft !== null) {
    const urgent = daysLeft <= 5;
    return (
      <Banner testid="trial-banner-countdown" tone={urgent ? "warn" : "info"} Icon={Sparkles}
        text={daysLeft > 0
          ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your free 14-day RollOSS trial · no charge until then.`
          : "Your RollOSS trial ends today."} />
    );
  }
  return null;
}

function Banner({ testid, tone, Icon, text }) {
  const palette = {
    danger: { bg: "rgba(255,80,80,0.12)", border: "#ff5050", fg: "rgb(255,140,140)" },
    warn: { bg: "rgba(244,185,66,0.12)", border: "#f4b942", fg: "rgb(244,185,66)" },
    info: { bg: "var(--green-bg)", border: "var(--green-border)", fg: "var(--green)" },
  };
  const colors = palette[tone] || palette.info;
  return (
    <div data-testid={testid} className="flex items-center justify-between gap-3 px-4 py-2 border-b"
      style={{ background: colors.bg, borderColor: colors.border, position: "sticky", top: 0, zIndex: 39 }}>
      <div className="flex items-center gap-2 min-w-0">
        <Icon size={14} style={{ color: colors.fg, flexShrink: 0 }} />
        <span className="label-tiny truncate" style={{ color: colors.fg }}>{text}</span>
      </div>
      <Link to="/app/plan" className="btn btn-outline shrink-0" style={{ borderColor: colors.border, color: colors.fg, padding: "4px 10px" }}
        data-testid="trial-banner-cta">
        View subscription
      </Link>
    </div>
  );
}
